import { apiClient } from '@/lib/api';

export type NivelDeAcceso = 'LECTURA' | 'EDICION';

/**
 * Cuál de las tres tablas de cotizaciones.
 *
 * Prixia guarda las cotizaciones normales, las del Pricer Datec y las del
 * Pricer Solar en tablas distintas. Compartir funciona igual en las tres; lo
 * único que cambia es a qué dirección se le pregunta.
 */
export type TipoDeCotizacion = 'QUOTE' | 'PRICER_DATEC' | 'PRICER_SOLAR';

const BASE: Record<TipoDeCotizacion, string> = {
  QUOTE: '/api/quotes',
  PRICER_DATEC: '/api/pricer-datec/quotes',
  PRICER_SOLAR: '/api/pricer-solar/quotes',
};

export interface AccesoListado {
  id: string | null;
  origen: 'DUENO' | 'PERSONA' | 'EQUIPO';
  nivel: NivelDeAcceso;
  quien: string;
  /** De dónde le viene el acceso, en llano. */
  porque: string;
  vence: string | null;
  sePuedeQuitar: boolean;
}

export interface RenglonDeHistorial {
  id: string;
  tipo: string;
  detalle: Record<string, unknown>;
  cuando: string;
  quien: string | null;
}

/**
 * Lo que responde el listado de accesos.
 *
 * ⚖️ `puedoCompartir` lo decide el SERVIDOR, no esta pantalla. Antes se
 * deducía acá con `createdById === usuario.id`, y se desincronizó: el backend
 * ya autorizaba al superadmin y la pantalla le escondía el botón. Una regla de
 * permisos escrita en los dos lados termina mintiendo en alguno.
 */
export interface AccesosDeLaCotizacion {
  accesos: AccesoListado[];
  puedoCompartir: boolean;
}

export const quoteAccessService = {
  listar: (quoteId: string, tipo: TipoDeCotizacion = 'QUOTE') =>
    apiClient.get<AccesosDeLaCotizacion>(`${BASE[tipo]}/${quoteId}/accesos`),

  compartir: (
    quoteId: string,
    datos: {
      granteeUserId?: string;
      granteeOrganizationId?: string;
      level: NivelDeAcceso;
      expiresAt?: string;
    },
    tipo: TipoDeCotizacion = 'QUOTE'
  ) => apiClient.post(`${BASE[tipo]}/${quoteId}/accesos`, datos),

  quitar: (quoteId: string, grantId: string, tipo: TipoDeCotizacion = 'QUOTE') =>
    apiClient.delete(`${BASE[tipo]}/${quoteId}/accesos/${grantId}`),

  historial: (quoteId: string, tipo: TipoDeCotizacion = 'QUOTE') =>
    apiClient.get<RenglonDeHistorial[]>(
      tipo === 'QUOTE'
        ? `${BASE[tipo]}/${quoteId}/historial`
        : `${BASE[tipo]}/${quoteId}/accesos/historial`
    ),
};
